import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ChevronLeft, ChevronRight, Users, ScrollText, AlertTriangle, CalendarCheck, Sparkles, FileSignature, TrendingUp, Clock } from "lucide-react";
import { DEMO_PAGE } from "@/constants/testIds";
import { Footer } from "@/components/Footer";

const STEPS = [
  {
    title: "Dashboard de Cumplimiento",
    desc: "Un semáforo (verde/ámbar/rojo) te dice de un vistazo si tu empresa está en orden: políticas pendientes de firmar, incidencias recientes y vacaciones sin resolver.",
    Mock: () => (
      <div className="bg-white rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse shrink-0" />
          <p className="text-xs font-semibold text-amber-700">Atención requerida</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[{ icon: Users, label: "Empleados", value: "24" }, { icon: ScrollText, label: "Políticas pendientes", value: "3" }, { icon: AlertTriangle, label: "Incidencias", value: "1" }, { icon: CalendarCheck, label: "Vacaciones por aprobar", value: "2" }].map((s, i) => (
            <div key={i} className="border border-petroleum/10 rounded-lg p-3">
              <s.icon className="w-4 h-4 text-petroleum mb-1.5" strokeWidth={1.5} />
              <p className="font-heading text-lg font-bold text-charcoal">{s.value}</p>
              <p className="text-[11px] text-charcoal/50">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: "Expediente de Empleado",
    desc: "Cada empleado tiene un expediente único: políticas confirmadas con fecha y hash inmutable, incidencias documentadas, vacaciones, salario con historial, y ahora también capacitación.",
    Mock: () => (
      <div className="bg-white rounded-xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-petroleum/10 flex items-center justify-center text-petroleum font-bold text-sm">JP</div>
          <div>
            <p className="text-sm font-semibold text-charcoal">Juan Pérez</p>
            <p className="text-xs text-charcoal/50">Operador de almacén · 2 años de antigüedad</p>
          </div>
        </div>
        <div className="flex gap-2 text-xs">
          {["Políticas", "Incidencias", "Vacaciones", "Salario"].map((t, i) => (
            <span key={t} className={`px-3 py-1.5 rounded-full ${i === 0 ? "bg-petroleum text-white" : "bg-petroleum/5 text-charcoal/60"}`}>{t}</span>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          {["Reglamento Interior — Confirmado 12/03/2026", "Política de Vacaciones — Pendiente de confirmar"].map((t) => (
            <div key={t} className="text-xs text-charcoal/70 border border-petroleum/10 rounded-lg p-2.5">{t}</div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: "Biblioteca de Documentos",
    desc: "Genera contratos, políticas y avisos con texto anclado a la LFT — con autollenado de los datos que ya capturaste. Firma física con respaldo digital para los documentos que la ley exige firmados a mano.",
    Mock: () => (
      <div className="bg-white rounded-xl p-5 space-y-2.5">
        {[{ t: "Contrato Individual por Tiempo Indeterminado", s: "Firma física", c: "text-terracotta" }, { t: "Política de Vacaciones", s: "Acuse de lectura", c: "text-petroleum" }, { t: "Aviso de Privacidad", s: "Acuse de lectura", c: "text-petroleum" }].map((d) => (
          <div key={d.t} className="flex items-center justify-between border border-petroleum/10 rounded-lg p-3">
            <div className="flex items-center gap-2.5">
              <FileSignature className={`w-4 h-4 ${d.c} shrink-0`} strokeWidth={1.5} />
              <p className="text-xs font-medium text-charcoal">{d.t}</p>
            </div>
            <span className="text-[10px] text-charcoal/40 shrink-0">{d.s}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "Vacaciones sin fricción",
    desc: "Cálculo automático de días conforme a los Art. 76 y 78 de la LFT (incluida la reforma de \"vacaciones dignas\"). El empleado solicita, tú apruebas, el saldo se actualiza solo.",
    Mock: () => (
      <div className="bg-white rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <CalendarCheck className="w-4 h-4 text-petroleum" strokeWidth={1.5} />
          <p className="text-sm font-semibold text-charcoal">Solicitud pendiente</p>
        </div>
        <div className="border border-petroleum/10 rounded-lg p-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-charcoal">María López — 5 días</p>
            <p className="text-[11px] text-charcoal/50">14 al 18 de septiembre, 2026</p>
          </div>
          <div className="flex gap-1.5">
            <span className="text-[11px] bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">Aprobar</span>
            <span className="text-[11px] bg-red-50 text-red-700 px-2.5 py-1 rounded-full">Rechazar</span>
          </div>
        </div>
        <p className="text-[11px] text-charcoal/40 mt-3">Saldo disponible: 16 de 16 días (2026)</p>
      </div>
    ),
  },
  {
    title: "Asesor IA de RH",
    desc: "Un consultor con criterio de RH real (no solo legal) te ayuda a pensar decisiones difíciles: retardos, desempeño, aumentos, conflictos — con el contexto real de tu empresa.",
    Mock: () => (
      <div className="bg-white rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-terracotta" />
          <p className="text-sm font-semibold text-charcoal">Consultor de RH</p>
        </div>
        <div className="bg-petroleum text-white text-xs rounded-2xl px-3.5 py-2.5 max-w-[85%] ml-auto">
          Un empleado con 2 años lleva 5 retardos este mes, ¿lo despido?
        </div>
        <div className="bg-accent text-charcoal text-xs rounded-2xl px-3.5 py-2.5 max-w-[90%]">
          Antes de decidir, verifica que tenga actas firmadas por el mismo motivo. Después habla con él —
          muchas veces hay una causa temporal detrás. Si es un buen empleado, la conversación...
        </div>
      </div>
    ),
  },
  {
    title: "Registro de Jornada",
    desc: "Reloj checador digital: cada empleado marca su entrada y salida desde su celular, o desde un kiosko compartido con PIN o QR en el centro de trabajo. Tú ves quién ya llegó hoy, sin tener que preguntar a nadie.",
    Mock: () => (
      <div className="bg-white rounded-xl p-5 space-y-2.5">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="w-4 h-4 text-petroleum" strokeWidth={1.5} />
          <p className="text-sm font-semibold text-charcoal">Quién ha marcado hoy</p>
        </div>
        {[
          { name: "Juan Pérez", time: "8:02 am", ok: true },
          { name: "María López", time: "8:15 am", ok: true },
          { name: "Carlos Ruiz", time: "Sin marcar", ok: false },
        ].map((r) => (
          <div key={r.name} className="flex items-center justify-between border border-petroleum/10 rounded-lg p-2.5">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full shrink-0 ${r.ok ? "bg-emerald-500" : "bg-red-500"}`} />
              <p className="text-xs font-medium text-charcoal">{r.name}</p>
            </div>
            <span className={`text-[11px] shrink-0 ${r.ok ? "text-charcoal/50" : "text-red-600 font-semibold"}`}>{r.time}</span>
          </div>
        ))}
      </div>
    ),
  },
];

export default function Demo() {
  const [step, setStep] = useState(0);
  const current = STEPS[step];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="flex items-center justify-between px-6 md:px-12 py-6">
        <Link to="/" className="flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-petroleum" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold text-charcoal tracking-tight">CERH</span>
        </Link>
        <Link to="/registro" className="text-sm font-semibold bg-petroleum text-white px-5 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all">
          Registrar mi empresa
        </Link>
      </header>

      <section className="flex-1 px-6 md:px-12 pb-16 max-w-5xl mx-auto w-full grid md:grid-cols-2 gap-10 items-center">
        <div>
          <p className="text-xs font-semibold text-terracotta uppercase tracking-wide mb-2">Recorrido guiado · sin necesidad de cuenta</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal leading-tight">{current.title}</h1>
          <p className="text-base text-charcoal/70 mt-4">{current.desc}</p>

          <div className="flex items-center gap-2 mt-8">
            {STEPS.map((_, i) => (
              <button
                key={i}
                data-testid={DEMO_PAGE.stepDot}
                onClick={() => setStep(i)}
                className={`h-2 rounded-full transition-all ${i === step ? "w-8 bg-terracotta" : "w-2 bg-petroleum/20"}`}
                aria-label={`Ir al paso ${i + 1}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-3 mt-6">
            <button
              data-testid={DEMO_PAGE.prevButton}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="flex items-center gap-1 text-sm font-medium text-charcoal/60 disabled:opacity-30 hover:text-charcoal transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Anterior
            </button>
            {step < STEPS.length - 1 ? (
              <button
                data-testid={DEMO_PAGE.nextButton}
                onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                className="flex items-center gap-1 text-sm font-semibold text-petroleum hover:gap-2 transition-all"
              >
                Siguiente <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <Link data-testid={DEMO_PAGE.ctaButton} to="/registro" className="text-sm font-semibold bg-terracotta text-white px-5 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all">
                Empezar con mi empresa
              </Link>
            )}
          </div>
        </div>

        <div className="bg-petroleum rounded-2xl p-6 shadow-[0_20px_50px_rgba(14,79,79,0.25)]">
          <current.Mock />
        </div>
      </section>
      <Footer />
    </div>
  );
}
